import { useEffect, useMemo, useRef, useState } from "react";
import { isKnownStateName, normalizeStateName, resolveStateName } from "../utils/locationState";

const env = (typeof process !== "undefined" && process.env) || {};

const resolveApiBase = () => {
  const envBase = env.REACT_APP_API_BASE_URL || env.REACT_APP_API_URL || "";
  if (typeof window === "undefined") return envBase || "";
  if (!envBase) return "";
  if (env.NODE_ENV !== "production" && envBase.replace(/\/$/, "") === window.location.origin.replace(/\/$/, "")) {
    return "";
  }
  return envBase.replace(/\/$/, "");
};

const apiPath = (path) => `${resolveApiBase()}${path}`;

const toQuery = (params) => {
  const search = new URLSearchParams(params);
  return search.toString();
};

export const useDebounce = (value, delay = 280) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return debounced;
};

const normalizeLocation = (item) => {
  const name =
    item?.display_name ||
    item?.name ||
    item?.city ||
    item?.label ||
    "";
  const city = item?.city || item?.name || name;
  const state = normalizeStateName(item?.state || item?.region || "");
  const country = item?.country || "";
  return {
    id: item?.id || name,
    city,
    state,
    country,
    display_name: name,
    property_count: Number(item?.property_count || 0),
    latitude: item?.latitude ?? null,
    longitude: item?.longitude ?? null,
    slug: item?.slug || "",
    type: item?.type || "city",
  };
};

const toTitleCase = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\b[a-z]/g, (char) => char.toUpperCase());

const normalizeKeyPart = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const buildCanonicalLocation = (item) => {
  const displayParts = String(item?.display_name || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const city = toTitleCase(item?.city || item?.name || displayParts[0] || "");
  const state = normalizeStateName(item?.state || item?.region || displayParts[1] || "");
  const country = toTitleCase(item?.country || displayParts[2] || (state ? "India" : ""));
  const canonicalParts = [city, state, country].filter(Boolean);

  return {
    city,
    state,
    country,
    displayName: canonicalParts.length ? canonicalParts.join(", ") : String(item?.display_name || "").trim(),
  };
};

export const dedupeLocationResults = (items) =>
  Array.from(
    items.reduce((map, item) => {
      const canonical = buildCanonicalLocation(item);
      const key = [
        normalizeKeyPart(canonical.city),
        normalizeKeyPart(canonical.state),
        normalizeKeyPart(canonical.country),
      ]
        .filter(Boolean)
        .join("|");
      if (!key) return map;

      const existing = map.get(key);
      if (existing) {
        map.set(key, {
          ...existing,
          property_count: Number(existing.property_count || 0) + Number(item?.property_count || 0),
        });
        return map;
      }

      map.set(key, {
        ...item,
        city: canonical.city,
        state: canonical.state,
        country: canonical.country,
        display_name: canonical.displayName,
      });
      return map;
    }, new Map()).values()
  );

const parseItems = (payload) => {
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const propertySearchTerm = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return isKnownStateName(raw) ? normalizeStateName(raw) : raw;
};

const addLocationCount = (map, key, item) => {
  if (!key || !item?.display_name) return;
  const existing = map.get(key);
  if (existing) {
    map.set(key, {
      ...existing,
      property_count: Number(existing.property_count || 0) + Number(item.property_count || 1),
    });
    return;
  }
  map.set(key, item);
};

export const locationsFromProperties = (properties = []) => {
  const map = new Map();
  properties.forEach((property) => {
    const state = normalizeStateName(property?.state || property?.region || "");
    const country = property?.country || (state ? "India" : "");
    const city = toTitleCase(property?.city || "");
    const area = toTitleCase(property?.area || "");

    if (state) {
      addLocationCount(map, `state:${state.toLowerCase()}`, {
        id: `state:${state.toLowerCase()}`,
        city: state,
        state,
        country,
        display_name: state,
        property_count: 1,
        latitude: null,
        longitude: null,
        slug: state.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""),
        type: "state",
      });
    }

    if (city) {
      addLocationCount(map, `city:${city.toLowerCase()}|${state.toLowerCase()}`, {
        id: `city:${city.toLowerCase()}|${state.toLowerCase()}`,
        city,
        state,
        country,
        display_name: [city, state, country].filter(Boolean).join(", "),
        property_count: 1,
        latitude: property?.latitude ?? null,
        longitude: property?.longitude ?? null,
        slug: city.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""),
        type: "city",
      });
    }

    if (area && area.toLowerCase() !== city.toLowerCase() && area.toLowerCase() !== state.toLowerCase()) {
      addLocationCount(map, `area:${area.toLowerCase()}|${city.toLowerCase()}|${state.toLowerCase()}`, {
        id: `area:${area.toLowerCase()}|${city.toLowerCase()}|${state.toLowerCase()}`,
        city: area,
        state: [city, state].filter(Boolean).join(", "),
        country,
        display_name: [area, city, state, country].filter(Boolean).join(", "),
        property_count: 1,
        latitude: property?.latitude ?? null,
        longitude: property?.longitude ?? null,
        slug: area.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""),
        type: "area",
      });
    }
  });
  return Array.from(map.values()).sort((a, b) => {
    const typeRank = { state: 0, city: 1, area: 2 };
    const rankDiff = (typeRank[a.type] ?? 3) - (typeRank[b.type] ?? 3);
    if (rankDiff) return rankDiff;
    return Number(b.property_count || 0) - Number(a.property_count || 0);
  });
};

export const fetchPropertyLocations = async (searchText, signal, limit = 160) => {
  const params = { limit };
  const term = propertySearchTerm(searchText);
  if (term) params.destination = term;
  const queryString = toQuery(params);
  const response = await fetch(apiPath(`/api/public/properties?${queryString}`), {
    signal,
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) return [];
  const payload = await response.json();
  return locationsFromProperties(parseItems(payload));
};

const aggregateByState = (items) =>
  Array.from(
    items.reduce((map, item) => {
      const stateName = resolveStateName(item, item?.display_name || item?.city || item?.name);
      if (!stateName) return map;
      const key = stateName.toLowerCase();
      const existing = map.get(key);
      if (existing) {
        map.set(key, {
          ...existing,
          property_count: Number(existing.property_count || 0) + Number(item?.property_count || 0),
        });
        return map;
      }
      map.set(key, {
        ...item,
        id: `state:${key}`,
        city: stateName,
        state: stateName,
        display_name: stateName,
        slug: key.replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""),
        type: "state",
      });
      return map;
    }, new Map()).values()
  );

export const addExactRegionResult = (items, searchText) => {
  const query = String(searchText || "").trim();
  if (!query) return items;

  const normalizedQuery = query.toLocaleLowerCase();
  const hasExactResult = items.some(
    (item) => String(item?.display_name || "").trim().toLocaleLowerCase() === normalizedQuery
  );
  if (hasExactResult) return items;

  const regionMatch = items.find(
    (item) => String(item?.state || "").trim().toLocaleLowerCase() === normalizedQuery
  );
  if (!regionMatch) return items;

  return [
    {
      id: `region:${normalizedQuery}`,
      city: regionMatch.state,
      state: regionMatch.state,
      country: regionMatch.country || "",
      display_name: regionMatch.state,
      property_count: items.reduce(
        (total, item) =>
          String(item?.state || "").trim().toLocaleLowerCase() === normalizedQuery
            ? total + Number(item?.property_count || 0)
            : total,
        0
      ),
      latitude: null,
      longitude: null,
      slug: normalizedQuery.replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""),
      type: "state",
    },
    ...items,
  ];
};

const fetchFirstSuccess = async (requests, signal) => {
  for (const request of requests) {
    try {
      const response = await fetch(request, { signal });
      if (!response.ok) continue;
      const data = await response.json();
      return parseItems(data);
    } catch (error) {
      if (error.name === "AbortError") throw error;
    }
  }
  return [];
};

export default function useLocationSearch(query, options = {}) {
  const normalized = String(query || "").trim();
  const debouncedQuery = useDebounce(normalized, 280);
  const popularLimit = Math.min(80, Math.max(1, Number(options.popularLimit) || 6));
  const searchLimit = Math.min(50, Math.max(1, Number(options.searchLimit) || 8));

  const [items, setItems] = useState([]);
  const [popular, setPopular] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const controllerRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    controllerRef.current?.abort();
    controllerRef.current = controller;

    const run = async () => {
      try {
        setError("");

        if (debouncedQuery.length < 2) {
          setLoading(true);
          const queryString = toQuery({ limit: popularLimit });
          const rawItems = await fetchFirstSuccess(
            [
              apiPath(`/api/search/popular?${queryString}`),
              apiPath(`/api/public/search-locations?${queryString}`),
              apiPath(`/api/search/locations?${queryString}`),
            ],
            controller.signal
          );
          const normalizedItems = rawItems.map(normalizeLocation).filter((item) => item.display_name);
          const propertyItems = await fetchPropertyLocations("", controller.signal, popularLimit * 8);
          setPopular(dedupeLocationResults([...aggregateByState(normalizedItems), ...propertyItems]));
          setItems([]);
          return;
        }

        setLoading(true);
        const queryString = toQuery({ q: debouncedQuery, limit: searchLimit });
        const rawItems = await fetchFirstSuccess(
          [
            apiPath(`/api/search/locations?${queryString}`),
            apiPath(`/api/public/search-locations?${queryString}`),
            apiPath(`/api/locations?${queryString}`),
          ],
          controller.signal
        );
        const normalizedItems = rawItems
          .map(normalizeLocation)
          .filter((item) => item.display_name);
        const propertyItems = await fetchPropertyLocations(debouncedQuery, controller.signal, searchLimit * 12);
        setItems(addExactRegionResult(dedupeLocationResults([...normalizedItems, ...propertyItems]), debouncedQuery));
      } catch (err) {
        if (err.name === "AbortError") return;
        setError(err.message || "Location search failed.");
        setItems([]);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    run();

    return () => controller.abort();
  }, [debouncedQuery, popularLimit, searchLimit]);

  useEffect(() => {
    return () => controllerRef.current?.abort();
  }, []);

  const suggestions = useMemo(() => {
    if (debouncedQuery.length < 2) return popular;
    return items;
  }, [debouncedQuery.length, items, popular]);

  return {
    suggestions,
    loading,
    error,
    isPopular: debouncedQuery.length < 2,
  };
}

